import type { Participant } from '@seen/core/models/patients/participant'
import {
  getLanguageDisplay,
  languageOptions,
  type ProfileLanguageCode,
} from '@seen/core/models/profile'
import type { ExtendedRelatedPerson } from '@seen/core/models/relatedPerson'
import { Button, type ButtonProps } from '@seen/ui'
import { IconStar, IconStarFilled } from '@tabler/icons-react'
import type { JSX, ReactElement } from 'react'
import { MultiSelect } from '#src/components/core/combobox/MultiSelect.tsx'
import { ResourceFieldForm } from '#src/components/core/form/ResourceFieldForm.tsx'

export interface LanguagesMultiSelectProps {
  profile: Participant | ExtendedRelatedPerson
  variant?: ButtonProps['variant']
  className?: string
  // Trigger content, computed from the current (unsaved) selection. Also
  // rendered while nothing is selected, so it must handle the empty case with
  // a prompt (e.g. "Add language") — the row must never visually vanish.
  renderTrigger: (value: ProfileLanguageCode[]) => ReactElement<any>
}

// Searchable multi-select over a profile's languages with a star accessory to
// mark one as preferred, autosaving through ResourceFieldForm.
export function LanguagesMultiSelect({
  profile,
  variant = 'underline',
  className,
  renderTrigger,
}: LanguagesMultiSelectProps): JSX.Element {
  return (
    <ResourceFieldForm
      instance={profile}
      valueFn={(profile) => profile.languageCodes}
      patchFn={(profile, values) => {
        profile.languageCodes = values
      }}
      render={({ field }) => {
        // Stored codes with no canonical option (legacy imports: "zh",
        // "vi", …) get their own row so the dropdown checks exactly what the
        // card and Languages section display — and so the entry can be
        // unselected at all. Unchecking one drops its row.
        const options = [
          ...languageOptions,
          ...field.value
            .filter((v) => !languageOptions.some((opt) => opt.value === v.code))
            .map((v) => ({
              value: v.code,
              label: getLanguageDisplay(v.code),
              preferred: false,
            })),
        ]
        const trigger = renderTrigger(field.value)
        return (
          <MultiSelect<{ value: string; label: string; preferred: boolean }>
            options={options}
            variant={variant}
            className={className}
            // The same trigger renders with an empty selection (via
            // MultiSelect's element-placeholder path), so the row keeps its
            // icon and shows the caller's empty-state prompt.
            placeholder={trigger}
            {...field}
            value={field.value.map((code) => code.code)}
            onChange={(codes) => {
              field.onChange(
                codes.map((code) => {
                  const option = options.find((opt) => opt.value === code)
                  const existing = field.value.find((v) => v.code === code)
                  return {
                    code,
                    display: option?.label ?? getLanguageDisplay(code),
                    preferred: existing?.preferred ?? false,
                  }
                })
              )
            }}
            renderAccessory={(option) => {
              const selected = field.value.find((v) => v.code === option.value)
              if (!selected) {
                return null
              }
              return (
                <Button
                  variant="ghost"
                  size="icon"
                  title={
                    selected.preferred
                      ? 'Unmark as preferred'
                      : 'Mark as preferred'
                  }
                  className="ml-auto size-4 p-0"
                  onClick={(e) => {
                    e.stopPropagation()
                    const codes =
                      field.value.map((v) =>
                        v.code === option.value
                          ? { ...v, preferred: !v.preferred }
                          : v
                      ) ?? []
                    field.onChange(codes)
                  }}
                >
                  {selected.preferred ? (
                    <IconStarFilled className="size-4" />
                  ) : (
                    <IconStar className="size-4" />
                  )}
                </Button>
              )
            }}
          >
            {trigger}
          </MultiSelect>
        )
      }}
    />
  )
}
